import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import SubLayout from '../components/layout/SubLayout';
import { aboutMenus } from '../data/aboutMenus';

const MapContainer = styled.div`
  width: 100%;
  height: 400px;
  background: #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    height: 280px;
  }
`;

const InfoTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 30px;
  font-size: 0.95rem;
`;

const InfoRow = styled.tr`
  border-bottom: 1px solid #eee;
`;

const InfoLabel = styled.td`
  width: 120px;
  padding: 16px 20px 16px 0;
  font-weight: 700;
  color: #1a2a40;
  vertical-align: top;
  white-space: nowrap;

  @media (max-width: 768px) {
    width: 90px;
    padding: 12px 10px 12px 0;
    font-size: 0.9rem;
  }
`;

const InfoValue = styled.td`
  padding: 16px 0;
  color: #444;
  line-height: 1.6;

  @media (max-width: 768px) {
    padding: 12px 0;
    font-size: 0.9rem;
  }
`;

const TransportSection = styled.div`
  margin-top: 50px;
`;

const TransportHeading = styled.h3`
  font-size: 1.3rem;
  color: #1a2a40;
  font-weight: 700;
  margin: 0 0 24px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
`;

const TransportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TransportCard = styled.div`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 24px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: #223E7E;
    box-shadow: 0 6px 20px rgba(34, 62, 126, 0.08);
  }
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const IconBadge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #eef2fa;
  color: #223E7E;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const CardTitle = styled.h4`
  font-size: 1.05rem;
  color: #222;
  font-weight: 700;
  margin: 0;
`;

const CardBody = styled.div`
  font-size: 0.92rem;
  color: #555;
  line-height: 1.75;
  word-break: keep-all;

  strong { color: #222; font-weight: 700; }
`;

const StopBlock = styled.div`
  &:not(:last-child) {
    margin-bottom: 14px;
    padding-bottom: 14px;
    border-bottom: 1px dashed #eee;
  }
`;

const StopName = styled.div`
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const RouteLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 5px;

  &:last-child { margin-bottom: 0; }
`;

const RouteTag = styled.span`
  flex-shrink: 0;
  min-width: 34px;
  text-align: center;
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;
  padding: 3px 8px;
  border-radius: 4px;
  background: ${props =>
    props.$type === '광역' ? '#c0392b' :
    props.$type === '마을' ? '#4e9a3f' :
    '#2769b3'};
`;

const RouteNums = styled.span`
  color: #555;
`;

const NaverMapLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 10px 20px;
  background: #03C75A;
  color: white;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s;

  &:hover { background: #02a84b; }
`;

// 두성시스템 주소: 경기도 의정부시 상금로 33 이롬프라자 506호
const LAT = 37.7577662;
const LNG = 127.0744898;

const Location = () => {
  const mapRef = useRef(null);

  useEffect(() => {
    // 네이버 지도 API 스크립트가 로드되어 있는지 확인
    if (!window.naver || !window.naver.maps) return;

    const map = new window.naver.maps.Map(mapRef.current, {
      center: new window.naver.maps.LatLng(LAT, LNG),
      zoom: 16,
    });

    new window.naver.maps.Marker({
      position: new window.naver.maps.LatLng(LAT, LNG),
      map,
      title: '주식회사 두성시스템',
    });
  }, []);

  return (
    <SubLayout title="오시는길" sidebarMenus={aboutMenus} bannerBg="/images/bg_building.jpg">
      <MapContainer ref={mapRef}>
        {/* 네이버 지도 API가 로드되지 않았을 때 fallback */}
        {(!window.naver || !window.naver.maps) && (
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            color: '#888', gap: '12px'
          }}>
            <div style={{ fontSize: '2.5rem' }}>🗺️</div>
            <p style={{ fontSize: '0.95rem' }}>지도를 불러오는 중입니다.</p>
            <NaverMapLink
              href="https://map.naver.com/p/search/%EA%B2%BD%EA%B8%B0%EB%8F%84%20%EC%9D%98%EC%A0%95%EB%B6%80%EC%8B%9C%20%EC%83%81%EA%B8%88%EB%A1%9C%2033"
              target="_blank"
              rel="noopener noreferrer"
            >
              🗺️ 네이버 지도에서 보기
            </NaverMapLink>
          </div>
        )}
      </MapContainer>

      <NaverMapLink
        href="https://map.naver.com/p/search/%EA%B2%BD%EA%B8%B0%EB%8F%84%20%EC%9D%98%EC%A0%95%EB%B6%80%EC%8B%9C%20%EC%83%81%EA%B8%88%EB%A1%9C%2033"
        target="_blank"
        rel="noopener noreferrer"
      >
        🗺️ 네이버 지도에서 크게 보기
      </NaverMapLink>

      <InfoTable>
        <tbody>
          <InfoRow>
            <InfoLabel>주소</InfoLabel>
            <InfoValue>경기도 의정부시 상금로 33 이롬프라자 506호</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>전화</InfoLabel>
            <InfoValue>031-871-4424 / 031-871-4425</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>팩스</InfoLabel>
            <InfoValue>031-871-4426</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>이메일</InfoLabel>
            <InfoValue>doosung0601@hanmail.net</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>업무시간</InfoLabel>
            <InfoValue>
              평일 09:00 ~ 18:00<br />
              <span style={{ color: '#999', fontSize: '0.85rem' }}>토·일·공휴일 휴무 (긴급시 별도 연락)</span>
            </InfoValue>
          </InfoRow>
        </tbody>
      </InfoTable>

      <TransportSection>
        <TransportHeading>대중교통 및 주차 안내</TransportHeading>
        <TransportGrid>
          <TransportCard>
            <CardHead>
              <IconBadge>🚇</IconBadge>
              <CardTitle>지하철</CardTitle>
            </CardHead>
            <CardBody>
              1호선 <strong>회룡역</strong> 하차 → 의정부경전철 환승 → <strong>효자역</strong> 하차 → 도보 약 10분
            </CardBody>
          </TransportCard>

          <TransportCard>
            <CardHead>
              <IconBadge>🚌</IconBadge>
              <CardTitle>버스</CardTitle>
            </CardHead>
            <CardBody>
              <StopBlock>
                <StopName>거성·세아아파트 하차</StopName>
                <RouteLine>
                  <RouteTag $type="마을">마을</RouteTag>
                  <RouteNums>207</RouteNums>
                </RouteLine>
              </StopBlock>
              <StopBlock>
                <StopName>세아·거성·벽산아파트 하차</StopName>
                <RouteLine>
                  <RouteTag $type="일반">일반</RouteTag>
                  <RouteNums>21, 33, 35, 118, 137, 138, 138-5</RouteNums>
                </RouteLine>
                <RouteLine>
                  <RouteTag $type="광역">광역</RouteTag>
                  <RouteNums>3200, 3500</RouteNums>
                </RouteLine>
                <RouteLine>
                  <RouteTag $type="마을">마을</RouteTag>
                  <RouteNums>206-1, 207, 207-1</RouteNums>
                </RouteLine>
              </StopBlock>
            </CardBody>
          </TransportCard>

          <TransportCard>
            <CardHead>
              <IconBadge>🅿️</IconBadge>
              <CardTitle>주차 안내</CardTitle>
            </CardHead>
            <CardBody>
              이롬프라자 <strong>지하 · 지상 주차장</strong> 무료 이용 가능
            </CardBody>
          </TransportCard>
        </TransportGrid>
      </TransportSection>
    </SubLayout>
  );
};

export default Location;
